import React, { useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { X } from "lucide-react";

interface ModalProps {
    title: string;
    onClose?: () => void;
    children: React.ReactNode;
}

// Modali aperte, in ordine di apertura: Esc chiude solo l'ultima.
const aperte: object[] = [];

// Montata nel body: dentro il layout lo sfondo restava sotto l'intestazione della pagina.
// Esc chiude come il clic fuori.
export const Modal: React.FC<ModalProps> = ({ title, onClose, children }) => {
    const id = useRef({}).current;
    const chiudi = useRef(onClose);
    chiudi.current = onClose;

    useEffect(() => {
        aperte.push(id);
        const onKey = (e: KeyboardEvent) => {
            if (e.key === 'Escape' && aperte[aperte.length - 1] === id) chiudi.current?.();
        };
        document.addEventListener('keydown', onKey);
        return () => {
            document.removeEventListener('keydown', onKey);
            const i = aperte.indexOf(id);
            if (i >= 0) aperte.splice(i, 1);
        };
    }, [id]);

    return createPortal(
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4" onClick={onClose}>
            <div
                className="bg-white rounded-xl shadow-2xl w-full max-w-lg overflow-hidden animate-in fade-in zoom-in-95 duration-200"
                onClick={e => e.stopPropagation()}
            >
                <div className="flex justify-between items-center p-5 border-b border-gray-100">
                    <h3 className="text-lg font-bold text-gray-800">{title}</h3>
                    {onClose && (
                        <button
                            onClick={onClose}
                            className="text-gray-400 hover:text-gray-600 p-1 rounded-full hover:bg-gray-100 transition-colors"
                        >
                            <X className="w-5 h-5" />
                        </button>
                    )}
                </div>
                <div className="p-5 max-h-[80vh] overflow-y-auto">
                    {children}
                </div>
            </div>
        </div>,
        document.body,
    );
};
